/**
 * Typed provider tenant management API facade over the generated contract client.
 */

import * as dt from '@cnapse/shared/data.types.js';
import { providerTenantsApi } from '../api/generated/providerTenants.api.js';
import { apiClient } from '../api/ApiClient.js';
import type {
  CreateTenantRequest,
  CreateTenantResponseData,
  CreateTenantUserRequest,
  CreateTenantUserResponseUserData,
  ProviderStatsResponseData,
  TenantsResponse,
  TenantResponse,
  TenantUpdateResponse,
  TenantUserData,
} from '@cnapse/shared/schemas.js';

export interface Tenant {
  id: dt.Uuid;
  name: string;
  subdomain: string;
  customerManagedEncryption?: boolean;
  subscriptionPlanId?: dt.IntegerId;
  createdAt?: dt.Date;
  updatedAt?: dt.Date;
}

class ProviderTenantsService {
  async getProviderStats(): Promise<ProviderStatsResponseData> {
    return providerTenantsApi.getStats() as Promise<ProviderStatsResponseData>;
  }

  async getTenants(): Promise<Tenant[]> {
    type ProviderTenantSummary = TenantsResponse['data']['tenants'][number];
    const tenants = await providerTenantsApi.list() as ProviderTenantSummary[];

    return tenants.map((tenant): Tenant => ({
      id: tenant.id.toString() as dt.Uuid,
      name: tenant.name,
      subdomain: tenant.subdomain,
      createdAt: tenant.createdAt,
      updatedAt: tenant.updatedAt,
    }));
  }

  async getTenant(id: dt.Uuid): Promise<Tenant> {
    type ProviderTenantDetail = TenantResponse['data']['tenant'];
    const tenant = await providerTenantsApi.getById(id) as ProviderTenantDetail;

    return {
      id: tenant.id.toString() as dt.Uuid,
      name: tenant.name,
      subdomain: tenant.subdomain,
      createdAt: tenant.createdAt,
      updatedAt: tenant.updatedAt,
    };
  }

  async createTenant(tenantData: CreateTenantRequest): Promise<CreateTenantResponseData> {
    return providerTenantsApi.create(tenantData) as Promise<CreateTenantResponseData>;
  }

  async deleteTenant(id: dt.Uuid, stepUpToken: string): Promise<void> {
    await apiClient.withStepUpToken(stepUpToken, () => providerTenantsApi.delete(id));
  }

  async resendTenantInvitation(id: dt.Uuid, stepUpToken: string): Promise<void> {
    await apiClient.withStepUpToken(stepUpToken, () => providerTenantsApi.resendInvitation(id));
  }

  async updateTenant(tenantId: dt.Uuid, updates: Partial<CreateTenantRequest>): Promise<Tenant> {
    type ProviderTenantUpdate = TenantUpdateResponse['data'];
    const tenant = await providerTenantsApi.update(tenantId, updates) as ProviderTenantUpdate;

    return {
      id: tenant.id,
      name: tenant.name,
      subdomain: tenant.subdomain,
      subscriptionPlanId: tenant.subscriptionPlanId,
      createdAt: tenant.createdAt,
      updatedAt: tenant.updatedAt,
    };
  }

  async getTenantUsers(tenantId: dt.Uuid): Promise<TenantUserData[]> {
    return providerTenantsApi.listUsers(tenantId) as Promise<TenantUserData[]>;
  }

  async createTenantUser(
    tenantId: dt.Uuid,
    userData: CreateTenantUserRequest,
  ): Promise<CreateTenantUserResponseUserData> {
    return providerTenantsApi.createUser(tenantId, userData) as Promise<CreateTenantUserResponseUserData>;
  }
}

export const providerTenantsService = new ProviderTenantsService();
